import { useState, useEffect, useCallback, useRef } from 'react';
import { useMutation } from 'react-query';
import { updatePaymentIntent } from '../api/subscription.api';
import { SubscriptionPayment } from '../../../core/store/slices/subscription.slice';

interface UsePaymentPollingOptions {
  // Controls whether polling is active
  isEnabled: boolean;
  // Maximum number of polling attempts before giving up
  maxAttempts?: number;
  // Base delay in milliseconds between polling attempts (will be multiplied by 2^attempt for backoff)
  baseDelay?: number;
  // Called when payment verification succeeds or reaches a terminal status
  onSuccess?: (data: any) => void;
  // Called when a polling attempt fails
  onError?: (error: any) => void;
  // Called when maximum polling attempts are reached but payment is still in a non-terminal state
  onMaxAttemptsReached?: () => void;
}

interface PaymentPollingResult {
  // Whether polling is currently active
  isPolling: boolean;
  // Whether the current polling request is in progress
  isLoading: boolean;
  // Number of polling attempts so far
  attempts: number;
  // Most recent data received from the payment verification endpoint
  lastData: any;
  // Function to manually start polling
  startPolling: () => void;
  // Function to manually stop polling
  stopPolling: () => void;
  // Function to reset and restart polling
  resetPolling: () => void;
}

/**
 * Custom hook that handles polling for payment status updates
 * with exponential backoff
 */
export const usePaymentPolling = ({
  isEnabled,
  maxAttempts = 5,
  baseDelay = 2000,
  onSuccess,
  onError,
  onMaxAttemptsReached
}: UsePaymentPollingOptions): PaymentPollingResult => {
  const [attempts, setAttempts] = useState(0);
  const [isPolling, setIsPolling] = useState(false);
  const [lastData, setLastData] = useState<SubscriptionPayment | null>(null);
  
  // Keep track of timers so we can clean them up
  const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Use React Query's useMutation for the API call
  const { mutate, isLoading, reset } = useMutation(
    () => updatePaymentIntent(),
    {
      onSuccess: (data) => {
        const subscriptionPayment = data.data.data;
        setLastData(subscriptionPayment);

        // Status of this PaymentIntent, one of 
        // requires_payment_method, 
        // requires_confirmation, 
        // requires_action, processing, 
        // requires_capture, 
        // canceled, or 
        // succeeded.
        
        // Check if payment has reached a terminal state
        if (subscriptionPayment.paymentStatus === 'succeeded' || subscriptionPayment.paymentStatus === 'failed' || subscriptionPayment.paymentStatus === 'canceled') {
          setIsPolling(false);
          onSuccess?.(subscriptionPayment);
        } else if (attempts >= maxAttempts - 1) {
          setIsPolling(false);
          onMaxAttemptsReached?.();
        } else {
          setAttempts(prev => prev + 1);
        }
      },
      onError: (error) => {
        if (attempts >= maxAttempts - 1) {
          setIsPolling(false);
          onMaxAttemptsReached?.();
        } else {
          setAttempts(prev => prev + 1);
        }
        
        onError?.(error);
      },
      retry: false
    }
  );
  
  const clearTimers = useCallback(() => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);
  
  const startPolling = useCallback(() => {
    if (!isEnabled) return;
    
    setIsPolling(true);
    setAttempts(0);
    reset();
    mutate();
  }, [isEnabled, mutate, reset]);
  
  useEffect(() => {
    if (!isPolling || attempts === 0) return;
    
    clearTimers();
    
    const nextDelay = baseDelay * Math.pow(2, attempts - 1);
    console.log(`Scheduling next payment check in ${nextDelay}ms (attempt ${attempts}/${maxAttempts})`);
    
    pollingTimerRef.current = setTimeout(() => {
      if (isPolling) {
        mutate();
      }
    }, nextDelay);
    
    return clearTimers;
  }, [attempts, isPolling, baseDelay, mutate, maxAttempts, clearTimers]);
  
  useEffect(() => {
    if (isEnabled && !isPolling && !isLoading) {
      startPolling();
    } else if (!isEnabled && isPolling) {
      setIsPolling(false);
      clearTimers();
    }
  }, [isEnabled, isPolling, isLoading, startPolling, clearTimers]);
  
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);
  
  return {
    isPolling,
    isLoading,
    attempts,
    lastData,
    startPolling,
    stopPolling: () => {
      setIsPolling(false);
      clearTimers();
    },
    resetPolling: startPolling
  };
};